import defaultStyled, { css as defaultCss, DefaultTheme } from 'styled-components';
import { Color, Theme } from 'src/utils/theme';
import createVariablesWrapper from 'src/components/createVariablesWrapper';
import { active, disable, hover } from 'src/components/utils';

export const ButtonColor = {
  GREEN: 'green',
  GREY: 'grey',
  RED: 'red',
} as const;
export type ButtonColor = (typeof ButtonColor)[keyof typeof ButtonColor];

export type ButtonStyleProps = {
  disabled: boolean;
  $buttonColor: ButtonColor;
  $highlight: boolean;
  $onVideo?: boolean;
};

type ButtonStyleVariables = {
  fontSize: string;
  borderRadius: string;
  width: string;
  height: string;
  padding: string;
  backgroundColor: string;
  hoverBackgroundColor: string;
  activeBackgroundColor: string;
  borderColor: string;
  labelColor: string;
  fontColor: string;
  hoverFontColor: string;
  activeFontColor: string;
  animationTiming: string;
};

type ButtonTheme = DefaultTheme & ButtonStyleVariables;

const styled = defaultStyled;
const css = defaultCss;

export type Selector = 'hover' | 'active';

type ApplySelector = (selector: Selector | undefined) => (color: string) => string;
const applySelector: ApplySelector = selector => color => {
  switch (selector) {
    case 'active':
      return active(color);
    case 'hover':
      return hover(color);
    case undefined:
      return color;
  }
};

type BaseColors = Record<
  'fontSecondary' | 'highlight' | 'border' | 'label',
  Record<ButtonColor, Color>
>;

const baseColors: BaseColors = {
  fontSecondary: {
    [ButtonColor.GREEN]: 'secondary0',
    [ButtonColor.GREY]: 'neutral20',
    [ButtonColor.RED]: 'error20',
  },
  highlight: {
    [ButtonColor.GREEN]: 'success80',
    [ButtonColor.GREY]: 'neutral98',
    [ButtonColor.RED]: 'error80',
  },
  border: {
    [ButtonColor.GREEN]: 'success40',
    [ButtonColor.GREY]: 'secondary80',
    [ButtonColor.RED]: 'error60',
  },
  label: {
    [ButtonColor.GREEN]: 'success40',
    [ButtonColor.GREY]: 'neutral20',
    [ButtonColor.RED]: 'error60',
  },
};

// Base/simple getters (those that use only color + props)

type BaseVarGetter = (theme: DefaultTheme) => (styleProps: ButtonStyleProps) => string;

const colorAccessor =
  (key: keyof BaseColors): BaseVarGetter =>
  ({ co }) =>
  ({ $buttonColor }) =>
    co[baseColors[key][$buttonColor]];

// Complex getters (those that also rely upon selectors)

type VarGetter = (
  theme: Theme,
) => (styleProps: ButtonStyleProps) => (selector?: Selector) => string;

export const getFontColor: VarGetter = theme => styleProps => selector => {
  const baseFontColor = colorAccessor('fontSecondary')(theme)(styleProps);
  const { disabled } = styleProps;
  if (disabled) return disable(baseFontColor);
  return applySelector(selector)(baseFontColor);
};

export const getBackgroundColor: VarGetter = theme => styleProps => selector => {
  const { $highlight, $onVideo } = styleProps;
  if (!$highlight) return $onVideo ? theme.co.neutral99 : 'none';

  const baseBackgroundColor = colorAccessor('highlight')(theme)(styleProps);

  const { disabled } = styleProps;
  if (disabled) return disable(baseBackgroundColor);

  return applySelector(selector)(baseBackgroundColor);
};

export const Variables = createVariablesWrapper<ButtonStyleProps, ButtonStyleVariables>(
  styleProps => theme => {
    const fontSize = theme.DEPRECATED_ty.medium;
    const borderRadius = theme.bo.small;
    const width = '100%';
    const height = 'auto';
    const padding = theme.sz.s4;

    const getThemedFontColor = getFontColor(theme)(styleProps);
    const fontColor = getThemedFontColor();
    const activeFontColor = getThemedFontColor('active');
    const hoverFontColor = getThemedFontColor('hover');

    const getThemedBGColor = getBackgroundColor(theme)(styleProps);
    const backgroundColor = getThemedBGColor();
    const activeBackgroundColor = getThemedBGColor('active');
    const hoverBackgroundColor = getThemedBGColor('hover');

    const borderColor = colorAccessor('border')(theme)(styleProps);
    const labelColor = colorAccessor('label')(theme)(styleProps);

    return {
      fontSize,
      borderRadius,
      width,
      height,
      padding,
      backgroundColor,
      activeBackgroundColor,
      hoverBackgroundColor,
      borderColor,
      labelColor,
      fontColor,
      activeFontColor,
      hoverFontColor,
      animationTiming: '200ms cubic-bezier(0, 1, 0.75, 1)',
      ...theme,
    };
  },
);

interface ButtonProps {
  disabled: boolean;
}

const buttonCss = css<ButtonProps>`
  /* Shared Styles */
  align-items: center;
  background: ${({ theme }) => (theme as ButtonTheme).backgroundColor};
  border-radius: ${({ theme }) => (theme as ButtonTheme).borderRadius};
  color: ${({ theme }) => (theme as ButtonTheme).fontColor};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  display: flex;
  flex-basis: 100%;
  flex-direction: row;
  font-family: ${({ theme }) => theme.DEPRECATED_ty.openSans};
  font-size: ${({ theme }) => (theme as ButtonTheme).fontSize};
  hyphens: auto;
  justify-content: center;
  line-height: 1.2;
  overflow-wrap: anywhere;
  padding: ${({ theme }) => (theme as ButtonTheme).padding};
  position: relative;
  text-align: center;
  text-decoration: none;
  transition:
    opacity ${({ theme }) => (theme as ButtonTheme).animationTiming},
    color 0.2s 0.1s ease-in-out;
  width: ${({ theme }) => (theme as ButtonTheme).width};
  height: ${({ theme }) => (theme as ButtonTheme).height};
  z-index: ${({ theme }) => theme.zi.z3};

  &:hover,
  &:hover:visited {
    background-color: ${({ theme }) => (theme as ButtonTheme).activeBackgroundColor};
    color: ${({ theme }) => theme.co.secondary0};
  }

  &:active,
  &:active:visited {
    box-shadow: none;
    color: ${({ theme }) => theme.co.secondary0};
    background-color: ${({ theme }) => (theme as ButtonTheme).activeBackgroundColor};
  }

  ${({ disabled, theme }) => css`
    border: ${theme.bo.mediumWidth} solid ${(theme as ButtonTheme).borderColor};

    /* We fake the 3d effect using a box-shadow */
    box-shadow: 0 4px 0 ${(theme as ButtonTheme).borderColor};
    font-weight: ${({ theme }) => theme.DEPRECATED_ty.semiBold};

    ${
      !disabled &&
      css`
        &:hover {
          box-shadow: 0 ${theme.sz.s2} 0 ${(theme as ButtonTheme).borderColor};
          transform: translateY(-4px);

          /* This is to avoid flickering when placing the cursor at the bottom, caused by the transform */
          &::after {
            content: '';
            width: 100%;
            position: absolute;
            top: 100%;
            height: 6px;
          }
        }
        &:active {
          box-shadow: 0 0 0 ${(theme as ButtonTheme).borderColor};
          transform: translateY(4px);
        }
      `
    }
  `}
`;

export const Button = styled.button<ButtonProps>`
  ${buttonCss}
`;

export const Container = styled.div`
  ${({ theme }) => `flex-basis: ${(theme as ButtonTheme).width};`}
  z-index: ${props => props.theme.zi.z1};
  position: relative;
`;

export const ButtonChildren = styled.div(
  ({ theme }) => css`
    flex-grow: 1;

    @media (min-width: 40rem) {
      margin-left: ${theme.sz.s10};
      margin-right: ${theme.sz.s10};
    }
  `,
);

export const PositionLabel = styled.div(
  ({ theme }) => css`
    display: none;
    background-color: ${(theme as ButtonTheme).labelColor};
    border-radius: ${theme.bo.extraSmall};
    color: ${
      (theme as ButtonTheme).backgroundColor === 'none'
        ? // 'none' is not a valid property for color so set a fallback off-white
          theme.co.primary99
        : (theme as ButtonTheme).backgroundColor
    };
    flex-shrink: 0;
    font-size: ${theme.DEPRECATED_ty.medium};
    width: ${theme.sz.s6};
    height: ${theme.sz.s6};
    justify-content: center;
    text-align: center;
    position: absolute;
    top: calc(50% - ${theme.sz.s3});
    left: ${theme.sz.s4};
    align-items: center;

    @media (min-width: 40rem) {
      display: flex;
    }
  `,
);
